import { useState } from "react";
import { Helmet } from "react-helmet";
import { LazyLoadImage } from "react-lazy-load-image-component";

import Footer from "../../Footer/FooterBottom";
import AdvancedSearchForm from "./components/AdvancedSearchForm";
import UsersTable from "./components/UsersTable";
import UserModals from "./components/UserModals";
import DownloadModal from "./components/DownloadModal";
import useUserFilters from "./hooks/useUserFilters";
import useUserActions from "./hooks/useUserActions";
import "./Users.css";

const UserList = () => {
    const {
        search, setSearch,
        filters, setFilters,
        handleSearchSync,
        handleClearFilters,
        promiseSchoolOptions,
        promiseClassRoomOptions,
        usersList, isUsersLoaded, toRemoveList,
        t,
    } = useUserFilters();

    const {
        activeModal, activeSubModal, activeUser,
        handleCloseModal,
        handleSubmitEditProfile,
        handleSubmitChangePassword,
        handleSubmitLoginInfo,
        handleSubmitChangeSchool,
        handleSubmitChangeClassRoom,
        handleSubmitChangeUserTypeWithSchool,
        handleActionTrigger,
        schoolModalOpen,
        selectedSchool,
        schoolUsers,
        loadingSchoolUsers,
        handleCloseSchoolModal,
    } = useUserActions({ filters, handleSearchSync });

    const [isAdvSearchOpen, setIsAdvSearchOpen] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);

    return (
        <>
            <section className="users-page-section">
                <div className="top-shape-center">
                    <LazyLoadImage src="/assets/css/img/gallery1.png" alt={`${t("alarabeyya")}`} />
                </div>
                <div className="bottom-shape-left">
                    <LazyLoadImage src="/assets/css/img/bottom-shape.png" alt={`${t("alarabeyya")}`} />
                </div>

                <div className="container-fluid px-lg-5 users-container">
                    <Helmet>
                        <title>{t("Pages.UsersControl.title")} | {t("alarabeyya")}</title>
                    </Helmet>

                    <div className="row justify-content-center">
                        <div className="col-lg-12">
                            {/* Page Header */}
                            <div className="users-page-header">
                                <h2 className="users-page-title">{t("Pages.UsersControl.title")}</h2>
                                <div className="users-title-accent"></div>
                            </div>

                            {/* Toolbar: Search + Count + Download */}
                            <div className="users-toolbar-wrapper w-100 mb-4">
                                <div className="users-toolbar d-flex align-items-center justify-content-between gap-3 flex-wrap">
                                    <div className="users-toolbar-search d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: '520px' }}>
                                        <div className="search-box-container flex-grow-1">
                                            <div className="search-box-inner">
                                                <i className="fas fa-search search-box-icon"></i>
                                                <input
                                                    type="text"
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    value={search}
                                                    placeholder={t("Pages.UsersControl.search")}
                                                    className="search-box-input"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className={`btn adv-search-toggle-btn d-flex align-items-center justify-content-center px-3 gap-2 ${isAdvSearchOpen ? 'active' : ''}`}
                                            onClick={() => setIsAdvSearchOpen(!isAdvSearchOpen)}
                                            title={t('Pages.UsersControl.toggleSearch')}
                                            style={{ borderRadius: '22px', height: '44px', width: '150px' }}
                                        >
                                            <i className="fal fa-sliders-h ml-2"></i>
                                            <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{t('Pages.UsersControl.toggleSearch')}</span>
                                        </button>
                                    </div>

                                    <div className="users-toolbar-actions align-self-start">
                                        {isUsersLoaded && (
                                            <div className="users-count-badge">
                                                <span className="users-count-label">{t("Pages.UsersControl.advancedSearch.total")}</span>
                                                <span className="users-count-value">{usersList.length}</span>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2"
                                            onClick={() => setShowDownloadModal(true)}
                                            style={{ height: '44px', borderRadius: '22px', fontWeight: '600' }}
                                        >
                                            <i className="fal fa-file-download ml-2"></i>
                                            <span>{t("Pages.UsersControl.export.title")}</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Advanced Search Filters - Renders full-width below the search/actions row */}
                                <AdvancedSearchForm
                                    show={isAdvSearchOpen}
                                    filters={filters}
                                    setFilters={setFilters}
                                    onSearch={() => handleSearchSync()}
                                    onClear={handleClearFilters}
                                    promiseSchoolOptions={promiseSchoolOptions}
                                    t={t}
                                />
                            </div>

                            {/* Users Table / Cards */}
                            <UsersTable
                                users={usersList}
                                isLoaded={isUsersLoaded}
                                toRemove={toRemoveList}
                                onAction={handleActionTrigger}
                                searchKey={search}
                                t={t}
                            />
                        </div>
                    </div>
                </div>
            </section>
            <Footer />

            {/* Administrative Modals */}
            <UserModals
                activeModal={activeModal}
                activeSubModal={activeSubModal}
                activeUser={activeUser}
                onClose={handleCloseModal}
                onSubmitEdit={handleSubmitEditProfile}
                onSubmitPassword={handleSubmitChangePassword}
                onSubmitLoginInfo={handleSubmitLoginInfo}
                onSubmitChangeSchool={
                    activeUser?.UserType?.Id === 2
                        ? handleSubmitChangeUserTypeWithSchool
                        : handleSubmitChangeSchool
                }
                onSubmitChangeClassRoom={handleSubmitChangeClassRoom}
                promiseSchoolOptions={promiseSchoolOptions}
                promiseClassRoomOptions={(inputValue, callback) =>
                    promiseClassRoomOptions(inputValue, callback, activeUser)
                }
                onAction={handleActionTrigger}
                schoolModalOpen={schoolModalOpen}
                selectedSchool={selectedSchool}
                schoolUsers={schoolUsers}
                loadingSchoolUsers={loadingSchoolUsers}
                onCloseSchoolModal={handleCloseSchoolModal}
                t={t}
            />

            {/* Download Modal */}
            <DownloadModal
                show={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                data={usersList}
                t={t}
            />
        </>
    );
};

export default UserList;
